import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import classes from '../glossary.module.scss';
import { createCn } from '../../../helpers/utils';
import { GlossaryTermItem } from '../../../services/api.service';

const cn = createCn('glossary', classes);

const LANGUAGE_LABELS: Record<string, string> = {
  ru: 'RU',
  kk: 'KK',
  en: 'EN',
};

interface GlossaryRowProps {
  term: GlossaryTermItem;
  togglingActive: boolean;
  deleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: (isActive: boolean) => void;
}

const GlossaryRow = ({ term, togglingActive, deleting, onEdit, onDelete, onToggleActive }: GlossaryRowProps) => {
  const { t } = useTranslation();
  const langKey = term.language ?? 'all';
  const langLabel = term.language ? LANGUAGE_LABELS[term.language] ?? term.language : t('glossary.language-all-badge');

  return (
    <div className={cn('row', { inactive: !term.is_active })}>
      <div className={cn('term')}>{term.term}</div>

      <div className={cn('description', { empty: !term.description })}>
        {term.description || '—'}
      </div>

      <div>
        <span className={cn('lang-badge', { [langKey]: true })}>{langLabel}</span>
      </div>

      <div className={cn('aliases')}>
        {term.aliases.length === 0 ? (
          <span className={cn('aliases-empty')}>—</span>
        ) : (
          term.aliases.map((alias) => (
            <span key={alias} className={cn('alias-chip')}>{alias}</span>
          ))
        )}
      </div>

      <label className={cn('switch')}>
        <input
          type="checkbox"
          checked={term.is_active}
          disabled={togglingActive}
          onChange={(e) => onToggleActive(e.target.checked)}
          aria-label={`${t('glossary.column-active')}: ${term.term}`}
        />
        <span className={cn('switch-track')} />
        <span className={cn('switch-thumb')} />
      </label>

      <div className={cn('actions')}>
        <button
          type="button"
          className={cn('icon-btn')}
          onClick={onEdit}
          title={t('glossary.edit') as string}
          aria-label={`${t('glossary.edit')}: ${term.term}`}
        >
          <Pencil size={15} />
        </button>
        <button
          type="button"
          className={cn('icon-btn', { danger: true })}
          onClick={onDelete}
          disabled={deleting}
          title={(deleting ? t('glossary.deleting') : t('glossary.delete')) as string}
          aria-label={`${deleting ? t('glossary.deleting') : t('glossary.delete')}: ${term.term}`}
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
};

export default GlossaryRow;
